import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import * as echarts from 'echarts'
import type { FeasibilityMetrics } from '@popety-io/types'
import { feasibility } from '@popety-io/utils'
import { Icon } from '../../../../../atoms/Icon'
import { safeChartInit } from '../../../../../../utils/echarts'
import { useAnalysisMode } from '../../../contexts'
import { ResidualValueSummary } from './ResidualValueSummary'
import {
  Container,
  SectionTitle,
  Grid,
  FullWidthSection,
  // Assessment
  AssessmentContainer,
  StatusSection,
  TrafficLight,
  StatusText,
  StatusTitle,
  StatusDescription,
  MetricsPreview,
  MetricItemRight,
  MetricLabelSmall,
  MetricValueLarge,
  // Summary Cards
  CardsGrid,
  Card,
  CardLabel,
  CardValue,
  CardSubtext,
  // Key Metrics
  Panel,
  MetricRow,
  MetricInfo,
  MetricLabel,
  MetricDescription,
  MetricValue,
  StatusBadge,
  SectionHeader,
  // Quick Facts
  FactsGrid,
  FactCard,
  FactIcon,
  FactContent,
  FactLabel,
  FactValue,
  // Chart
  ChartContainer,
  // Empty
  EmptyContainer,
  EmptyTitle,
  EmptyDescription,
} from './FeasibilityStudyTab.styles'

// =====================================================
// Swiss number formatters
// =====================================================

const formatNumber = (value: number, decimals: number = 0): string => {
  if (isNaN(value)) return '0'
  const rounded =
    decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString()
  const [integerPart, decimalPart] = rounded.split('.')
  const formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'")
  return decimalPart ? `${formatted}.${decimalPart}` : formatted
}

const formatCurrency = (value: number): string => {
  if (isNaN(value)) return 'CHF 0'
  return `CHF ${formatNumber(value)}`
}

const formatPercentage = (value: number): string => {
  if (isNaN(value)) return '0.00%'
  return `${value.toFixed(2)}%`
}

const formatCompact = (value: number): string => {
  if (isNaN(value)) return '0'
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`
  return value.toString()
}

// =====================================================
// Sub-components
// =====================================================

const FeasibilityAssessment: React.FC<{ metrics: FeasibilityMetrics }> = ({
  metrics,
}) => {
  const { t } = useTranslation('feasibility')
  const status = metrics.feasibility_status
  const target =
    metrics.target_developer_profit_pct ??
    feasibility.DEFAULT_TARGET_DEVELOPER_PROFIT_PCT
  const marginal = target - feasibility.DEVELOPER_MARGIN_MARGINAL_GAP_PCT

  const getStatusIcon = () => {
    switch (status) {
      case 'viable':
        return '\u2713'
      case 'marginal':
        return '!'
      case 'not_viable':
        return '\u2717'
    }
  }

  return (
    <AssessmentContainer $status={status}>
      <StatusSection>
        <TrafficLight $status={status}>{getStatusIcon()}</TrafficLight>
        <StatusText>
          <StatusTitle $status={status}>
            {t(`assessment.${status}.title`)}
          </StatusTitle>
          <StatusDescription>
            {t(`assessment.${status}.description`, { target, marginal })}
          </StatusDescription>
        </StatusText>
      </StatusSection>

      <MetricsPreview>
        <MetricItemRight>
          <MetricLabelSmall>{t('metrics.developerMargin')}</MetricLabelSmall>
          <MetricValueLarge>
            {formatPercentage(metrics.developer_margin_percentage)}
          </MetricValueLarge>
        </MetricItemRight>
        <MetricItemRight>
          <MetricLabelSmall>{t('metrics.roiOnEquity')}</MetricLabelSmall>
          <MetricValueLarge>
            {formatPercentage(metrics.roi_on_equity)}
          </MetricValueLarge>
        </MetricItemRight>
        {metrics.residual && (
          <MetricItemRight>
            <MetricLabelSmall>{t('residual.maxBid')}</MetricLabelSmall>
            <MetricValueLarge>
              {formatCurrency(metrics.residual.residual_value)}
            </MetricValueLarge>
          </MetricItemRight>
        )}
      </MetricsPreview>
    </AssessmentContainer>
  )
}

const FeasibilitySummaryCards: React.FC<{ metrics: FeasibilityMetrics }> = ({
  metrics,
}) => {
  const { t } = useTranslation('feasibility')

  return (
    <CardsGrid>
      <Card>
        <CardLabel>{t('summaryCards.totalRevenue')}</CardLabel>
        <CardValue $positive={metrics.total_revenue > 0}>
          {formatCurrency(metrics.total_revenue)}
        </CardValue>
        <CardSubtext>{t('summaryCards.fromSales')}</CardSubtext>
      </Card>

      <Card>
        <CardLabel>{t('summaryCards.totalCosts')}</CardLabel>
        <CardValue>{formatCurrency(metrics.total_costs)}</CardValue>
        <CardSubtext>
          {t('summaryCards.costBreakdown', {
            land: formatCurrency(metrics.land_costs),
            construction: formatCurrency(metrics.construction_costs),
          })}
        </CardSubtext>
      </Card>

      <Card>
        <CardLabel>{t('summaryCards.financingCosts')}</CardLabel>
        <CardValue>{formatCurrency(metrics.total_financing_costs)}</CardValue>
        <CardSubtext>{t('summaryCards.interestAndFees')}</CardSubtext>
      </Card>

      <Card>
        <CardLabel>{t('summaryCards.netProfit')}</CardLabel>
        <CardValue
          $positive={metrics.net_profit > 0}
          $negative={metrics.net_profit < 0}
        >
          {formatCurrency(metrics.net_profit)}
        </CardValue>
        <CardSubtext>
          {t('summaryCards.grossProfit', {
            gross: formatCurrency(metrics.gross_profit),
          })}
        </CardSubtext>
      </Card>
    </CardsGrid>
  )
}

const WaterfallChart: React.FC<{ metrics: FeasibilityMetrics }> = ({
  metrics,
}) => {
  const { t } = useTranslation('feasibility')
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useEffect(() => {
    if (!chartRef.current) return

    chartInstance.current = safeChartInit(chartRef.current)

    const categories = [
      t('waterfall.revenue'),
      t('waterfall.landCosts'),
      t('waterfall.construction'),
      t('waterfall.financing'),
      t('waterfall.netProfit'),
    ]

    const landCost = metrics.land_costs
    const constructionCost = metrics.construction_costs
    const financingCost = metrics.total_financing_costs
    const revenue = metrics.total_revenue
    const netProfit = metrics.net_profit

    const placeholders = [
      0,
      revenue - landCost,
      revenue - landCost - constructionCost,
      revenue - landCost - constructionCost - financingCost,
      0,
    ]

    const barHeights = [
      revenue,
      landCost,
      constructionCost,
      financingCost,
      Math.abs(netProfit),
    ]

    const colors = [
      '#10B981',
      '#EF4444',
      '#EF4444',
      '#EF4444',
      netProfit >= 0 ? '#10B981' : '#EF4444',
    ]

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) => {
          const item = params[1] || params[0]
          const value = barHeights[item.dataIndex]
          const label = categories[item.dataIndex]
          return `${label}<br/><strong>${formatCurrency(value)}</strong>`
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: { fontSize: 12, color: '#6B7280' },
        axisLine: { lineStyle: { color: '#E5E7EB' } },
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: number) => formatCompact(value),
          fontSize: 11,
          color: '#6B7280',
        },
        splitLine: { lineStyle: { color: '#F3F4F6' } },
      },
      series: [
        {
          name: 'Base',
          type: 'bar',
          stack: 'Total',
          itemStyle: { borderColor: 'transparent', color: 'transparent' },
          emphasis: {
            itemStyle: { borderColor: 'transparent', color: 'transparent' },
          },
          data: placeholders,
        },
        {
          name: 'Value',
          type: 'bar',
          stack: 'Total',
          data: barHeights.map((val, idx) => ({
            value: val,
            itemStyle: { color: colors[idx], borderRadius: [4, 4, 0, 0] },
            label: {
              show: true,
              position: 'top',
              formatter: formatCurrency(val),
              fontSize: 11,
              color: '#374151',
            },
          })),
        },
      ],
    }

    chartInstance.current.setOption(option)

    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [metrics, t])

  return <ChartContainer ref={chartRef} />
}

const KeyMetricsPanel: React.FC<{ metrics: FeasibilityMetrics }> = ({
  metrics,
}) => {
  const { t } = useTranslation('feasibility')

  const getMarginStatus = (margin: number): 'good' | 'warning' | 'bad' => {
    const target =
      metrics.target_developer_profit_pct ??
      feasibility.DEFAULT_TARGET_DEVELOPER_PROFIT_PCT
    const status = feasibility.developerMarginStatus(margin, target)
    if (status === 'viable') return 'good'
    if (status === 'marginal') return 'warning'
    return 'bad'
  }

  const getRoiStatus = (roi: number): 'good' | 'warning' | 'bad' => {
    if (roi >= 20) return 'good'
    if (roi >= 10) return 'warning'
    return 'bad'
  }

  const getMultipleStatus = (multiple: number): 'good' | 'warning' | 'bad' => {
    if (multiple >= 1.2) return 'good'
    if (multiple >= 1.1) return 'warning'
    return 'bad'
  }

  const marginTone = getMarginStatus(metrics.developer_margin_percentage)
  const targetPct =
    metrics.target_developer_profit_pct ??
    feasibility.DEFAULT_TARGET_DEVELOPER_PROFIT_PCT

  return (
    <Panel>
      <SectionHeader>{t('keyMetrics.developerMetrics')}</SectionHeader>

      <MetricRow>
        <MetricInfo>
          <MetricLabel>
            {t('metrics.developerMargin')}
            <StatusBadge $status={marginTone}>
              {marginTone === 'good'
                ? t('keyMetrics.targetMet')
                : marginTone === 'warning'
                  ? t('keyMetrics.belowTarget')
                  : t('keyMetrics.critical')}
            </StatusBadge>
          </MetricLabel>
          <MetricDescription>
            {t('keyMetrics.targetLabel', { target: targetPct })}
          </MetricDescription>
        </MetricInfo>
        <MetricValue
          $status={getMarginStatus(metrics.developer_margin_percentage)}
        >
          {formatPercentage(metrics.developer_margin_percentage)}
        </MetricValue>
      </MetricRow>

      <MetricRow>
        <MetricInfo>
          <MetricLabel>{t('metrics.roiOnEquity')}</MetricLabel>
          <MetricDescription>
            {t('keyMetrics.netProfitEquity')}
          </MetricDescription>
        </MetricInfo>
        <MetricValue $status={getRoiStatus(metrics.roi_on_equity)}>
          {formatPercentage(metrics.roi_on_equity)}
        </MetricValue>
      </MetricRow>

      <MetricRow>
        <MetricInfo>
          <MetricLabel>{t('metrics.profitMultiple')}</MetricLabel>
          <MetricDescription>
            {t('keyMetrics.revenueInvestment')}
          </MetricDescription>
        </MetricInfo>
        <MetricValue $status={getMultipleStatus(metrics.profit_multiple)}>
          {formatNumber(metrics.profit_multiple, 2)}x
        </MetricValue>
      </MetricRow>

      {metrics.irr !== undefined && (
        <MetricRow>
          <MetricInfo>
            <MetricLabel>{t('metrics.irr')}</MetricLabel>
            <MetricDescription>
              {t('keyMetrics.internalRateOfReturn')}
            </MetricDescription>
          </MetricInfo>
          <MetricValue $status={getRoiStatus(metrics.irr)}>
            {formatPercentage(metrics.irr)}
          </MetricValue>
        </MetricRow>
      )}

      <SectionHeader>{t('keyMetrics.investmentOverview')}</SectionHeader>

      <MetricRow>
        <MetricInfo>
          <MetricLabel>{t('metrics.equityInvested')}</MetricLabel>
          <MetricDescription>
            {t('keyMetrics.totalEquityInProject')}
          </MetricDescription>
        </MetricInfo>
        <MetricValue>
          CHF {formatNumber(metrics.total_equity_invested, 0)}
        </MetricValue>
      </MetricRow>
    </Panel>
  )
}

const QuickFactsPanel: React.FC<{
  financingData: any
  revenueData: any
  metrics: FeasibilityMetrics
}> = ({ financingData, revenueData, metrics }) => {
  const { t } = useTranslation('feasibility')
  const na = t('quickFacts.notAvailable')

  const data = revenueData as any
  const projectType = data?.project_config?.project_type || null

  let totalUnits = 0
  let totalArea = 0

  if (data?.revenue_summary?.total_units) {
    totalUnits = data.revenue_summary.total_units
  } else if (data?.total_units) {
    totalUnits = data.total_units
  } else if (data?.buildings && Array.isArray(data.buildings)) {
    for (const building of data.buildings) {
      if (building.floors && Array.isArray(building.floors)) {
        for (const floor of building.floors) {
          if (floor.units && Array.isArray(floor.units)) {
            totalUnits += floor.units.length
          }
        }
      }
    }
  }

  if (data?.revenue_summary?.total_saleable_area) {
    totalArea = data.revenue_summary.total_saleable_area
  } else if (data?.total_saleable_area) {
    totalArea = data.total_saleable_area
  } else if (data?.buildings && Array.isArray(data.buildings)) {
    for (const building of data.buildings) {
      if (building.floors && Array.isArray(building.floors)) {
        for (const floor of building.floors) {
          if (floor.units && Array.isArray(floor.units)) {
            for (const unit of floor.units) {
              totalArea += unit.area_m2 || 0
            }
          }
        }
      }
    }
  }

  const pricePerM2 = totalArea > 0 ? metrics.total_revenue / totalArea : 0

  const planningMonths = financingData?.timeline?.planning_phase_months || 0
  const constructionMonths =
    financingData?.timeline?.construction_phase_months || 0
  const totalMonths = planningMonths + constructionMonths

  const landFinancing = metrics.land_costs || 0
  const constructionFinancing = metrics.construction_costs || 0
  const totalFinancing = landFinancing + constructionFinancing
  const landPercentage =
    totalFinancing > 0 ? (landFinancing / totalFinancing) * 100 : 0

  return (
    <FactsGrid>
      <FactCard>
        <FactIcon>
          <Icon name="building" size="medium" />
        </FactIcon>
        <FactContent>
          <FactLabel>{t('quickFacts.projectType')}</FactLabel>
          <FactValue style={{ textTransform: 'capitalize' }}>
            {projectType || na}
          </FactValue>
        </FactContent>
      </FactCard>

      <FactCard>
        <FactIcon>
          <Icon name="home" size="medium" />
        </FactIcon>
        <FactContent>
          <FactLabel>{t('quickFacts.totalUnits')}</FactLabel>
          <FactValue>{totalUnits > 0 ? totalUnits : na}</FactValue>
        </FactContent>
      </FactCard>

      <FactCard>
        <FactIcon>
          <Icon name="ruler" size="medium" />
        </FactIcon>
        <FactContent>
          <FactLabel>{t('quickFacts.saleableArea')}</FactLabel>
          <FactValue>
            {totalArea > 0 ? `${formatNumber(totalArea, 0)} m\u00B2` : na}
          </FactValue>
        </FactContent>
      </FactCard>

      <FactCard>
        <FactIcon>
          <Icon name="dollar-sign" size="medium" />
        </FactIcon>
        <FactContent>
          <FactLabel>{t('quickFacts.pricePerM2')}</FactLabel>
          <FactValue>
            {pricePerM2 > 0 ? formatCurrency(Math.round(pricePerM2)) : na}
          </FactValue>
        </FactContent>
      </FactCard>

      <FactCard>
        <FactIcon>
          <Icon name="calendar" size="medium" />
        </FactIcon>
        <FactContent>
          <FactLabel>{t('quickFacts.projectDuration')}</FactLabel>
          <FactValue>
            {totalMonths > 0
              ? t('quickFacts.durationValue', {
                  months: totalMonths,
                  years: (totalMonths / 12).toFixed(1),
                })
              : na}
          </FactValue>
        </FactContent>
      </FactCard>

      <FactCard>
        <FactIcon>
          <Icon name="clock" size="medium" />
        </FactIcon>
        <FactContent>
          <FactLabel>{t('quickFacts.timelineSplit')}</FactLabel>
          <FactValue>
            {planningMonths > 0 || constructionMonths > 0
              ? t('quickFacts.timelineValue', {
                  planning: planningMonths,
                  build: constructionMonths,
                })
              : na}
          </FactValue>
        </FactContent>
      </FactCard>

      <FactCard>
        <FactIcon>
          <Icon name="landmark" size="medium" />
        </FactIcon>
        <FactContent>
          <FactLabel>{t('quickFacts.landFinancing')}</FactLabel>
          <FactValue>
            {landFinancing > 0
              ? `${formatCurrency(landFinancing)} (${formatNumber(landPercentage, 0)}%)`
              : na}
          </FactValue>
        </FactContent>
      </FactCard>

      <FactCard>
        <FactIcon>
          <Icon name="coins" size="medium" />
        </FactIcon>
        <FactContent>
          <FactLabel>{t('quickFacts.equityRequired')}</FactLabel>
          <FactValue>
            {metrics.total_equity_invested > 0
              ? formatCurrency(metrics.total_equity_invested)
              : na}
          </FactValue>
        </FactContent>
      </FactCard>
    </FactsGrid>
  )
}

// =====================================================
// Main Component
// =====================================================

export const FeasibilityStudyTab: React.FC = () => {
  const { t } = useTranslation('feasibility')
  const { entity } = useAnalysisMode()

  const feasibilityData = entity?.feasibility || null

  if (!feasibilityData?.metrics) {
    return (
      <EmptyContainer>
        <Icon name="check-circle" size="xl" color="secondary" />
        <EmptyTitle>{t('empty.title')}</EmptyTitle>
        <EmptyDescription>{t('empty.description')}</EmptyDescription>
      </EmptyContainer>
    )
  }

  const { metrics, financing_data, revenue_data } = feasibilityData

  return (
    <Container>
      <FeasibilityAssessment metrics={metrics} />
      {metrics.residual && <ResidualValueSummary residual={metrics.residual} />}

      <SectionTitle>{t('sections.financialSummary')}</SectionTitle>
      <FullWidthSection>
        <FeasibilitySummaryCards metrics={metrics} />
      </FullWidthSection>

      <Grid>
        <div>
          <SectionTitle>{t('sections.costBreakdown')}</SectionTitle>
          <WaterfallChart metrics={metrics} />
        </div>
        <div>
          <SectionTitle>{t('sections.keyPerformanceIndicators')}</SectionTitle>
          <KeyMetricsPanel metrics={metrics} />
        </div>
      </Grid>

      <SectionTitle>{t('sections.projectQuickFacts')}</SectionTitle>
      <QuickFactsPanel
        financingData={financing_data}
        revenueData={revenue_data}
        metrics={metrics}
      />
    </Container>
  )
}
